import React, { useMemo } from 'react';
import './Projects.css';
import { Link, useSearchParams } from 'react-router-dom';
import projectCatalogModule from '../../content/projectCatalog';

// Project Images
import barinakImg from '../../photos/projects/Barinak.jpg';
import xrpImg from '../../photos/projects/XRP.jpg';
import cubesatImg from '../../photos/projects/cubesat.png';
import tekyazImg from '../../photos/projects/tekyaz.png';
import dronImg from '../../photos/projects/dron.png';
import steamnearmeImg from '../../photos/projects/steamnearme.png';
import first101Img from '../../photos/projects/FIRST101.png';
import comicsImg from '../../photos/projects/comics.png';
import companionImg from '../../photos/projects/companion.png';
import gdImg from '../../photos/projects/GD.jpg';
import tedxImg from '../../photos/projects/tedx.jpg';
import girlsfirstImg from '../../photos/projects/girlsfirst.jpg';
import dreamsfirstImg from '../../photos/projects/dreamsfirst.png';
import uavCurriculumImg from '../../photos/projects/curriculum.jpeg';
import firstMuseumImg from '../../photos/projects/first-museum.jpg';
import firstStepImg from '../../photos/projects/first-step.jpeg';
import icampImg from '../../photos/projects/icamp.jpg';
import kickoffImg from '../../photos/projects/kickoff.JPG';
import nasaSpaceAppsImg from '../../photos/projects/nasa-space-apps.jpeg';
import integraiImg from '../../photos/projects/integrai.jpeg';
import jrArchaeologistsImg from '../../photos/projects/jr-archaeologist.jpeg';
import futureAiSummit2026Img from '../../photos/projects/Future-AI-Summit-2026.jpeg';

const {
  projectCatalog,
  getProjectPath,
} = projectCatalogModule;

const PROJECT_IMAGES = {
  'charity-campaign-pet-food-donation': barinakImg,
  'xrp-robotics-platform-with-wpi': xrpImg,
  'cubesat-project': cubesatImg,
  'cad-lessons-partnership': tekyazImg,
  'drone-laboratory': dronImg,
  'steam-near-me-platform': steamnearmeImg,
  'first-publications': first101Img,
  'steam-comics-initiative': comicsImg,
  'the-companion-e-zine': companionImg,
  'young-diplomats-conference': gdImg,
  'tedx-breaking-barriers': tedxImg,
  'girls-first-summit': girlsfirstImg,
  'dreams-first': dreamsfirstImg,
  'uav-curriculum': uavCurriculumImg,
  'first-museum': firstMuseumImg,
  'the-first-step': firstStepImg,
  icamp: icampImg,
  integrai: integraiImg,
  'jr-archaeologists': jrArchaeologistsImg,
  'frc-kickoff-istanbul': kickoffImg,
  'nasa-space-apps-challenge': nasaSpaceAppsImg,
  'future-ai-summit-2026': futureAiSummit2026Img,
};

const FIRST_TERM_ORDER = [
  'Hosted',
  'Ran an Event',
  'Published Resources',
  'Advocacy',
  'Assisted',
  'Supporting',
  'Mentored',
  'Started',
  'Reached',
];

export const projectsData = projectCatalog.map((project) => {
  const resources = Array.isArray(project.resources)
    ? project.resources
      .map((resource) => ({
        ...resource,
        href: resource.href || null,
      }))
      .filter((resource) => Boolean(resource.href))
    : undefined;

  return {
    ...project,
    image: PROJECT_IMAGES[project.slug],
    resources: resources?.length ? resources : undefined,
  };
});

const yearOptions = Array.from(
  new Set(projectsData.flatMap((project) => project.years || []))
).sort((a, b) => Number(b) - Number(a));

const termOptions = FIRST_TERM_ORDER.filter((term) => (
  projectsData.some((project) => project.firstTerms?.includes(term))
));

const featuredProjects = projectsData
  .filter((project) => Number.isFinite(project.featuredRank))
  .sort((a, b) => a.featuredRank - b.featuredRank);

const getProjectMetaTags = (project) => [
  project.periodLabel ? { type: 'period', label: project.periodLabel } : null,
  ...(project.firstTerms || []).slice(0, 2).map((term) => ({ type: 'first', label: term })),
].filter(Boolean).slice(0, 3);

export const ProjectCard = ({ project, variant = 'default' }) => {
  const metaTags = getProjectMetaTags(project);

  return (
    <article className={`project-card project-card--${variant}`}>
      <Link
        to={getProjectPath(project)}
        className="project-card__image-link"
        aria-label={`View ${project.title} project details`}
        tabIndex={-1}
      >
        <span className="project-card__image">
          <img
            src={project.image}
            alt={`${project.title} project`}
            loading="lazy"
          />
        </span>
      </Link>
      <div className="project-card__content">
        {metaTags.length > 0 && (
          <div className="project-card__meta" aria-label="Project classifications">
            {metaTags.map((tag) => (
              <span className={`project-tag project-tag--${tag.type}`} key={`${tag.type}-${tag.label}`}>
                {tag.label}
              </span>
            ))}
          </div>
        )}
        <h3 className="project-card__title">
          <span itemProp="name">{project.title}</span>
        </h3>
        <p className="project-card__description" itemProp="description">
          {project.shortDescription}
        </p>
        <Link
          to={getProjectPath(project)}
          className="project-card__link"
          aria-label={`View project details for ${project.title}`}
        >
          View Project <span aria-hidden="true">&rarr;</span>
        </Link>
      </div>
    </article>
  );
};

const FilterGroup = ({ legend, options, selectedValue, onSelect }) => (
  <fieldset className="projects-filter-group">
    <legend>
      <span>{legend}</span>
    </legend>
    <div className="projects-filter-options">
      {options.map((option) => (
        <button
          type="button"
          className="projects-filter-button"
          aria-pressed={selectedValue === option.value}
          key={option.value}
          onClick={() => onSelect(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </fieldset>
);

export const ProjectsPreview = () => {
  return (
    <section className="projects-preview" aria-labelledby="projects-preview-title">
      <header className="projects-preview__header">
        <div className="projects-preview__intro">
          <h2 id="projects-preview-title" className="projects-preview__eyebrow">INTEGRA Initiatives</h2>
          <p>
            Systems, resources, and experiences designed to expand access, capability, and continuity.
          </p>
        </div>
        <Link className="projects-preview__all-link" to="/projects">
          View All Projects
        </Link>
      </header>
      <div className="projects-preview__grid">
        {featuredProjects.map((project) => (
          <ProjectCard project={project} key={project.slug} />
        ))}
      </div>
    </section>
  );
};

const Projects = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedYear = searchParams.get('year');
  const requestedTerm = searchParams.get('term');
  const selectedYear = yearOptions.includes(requestedYear) ? requestedYear : 'all';
  const selectedTerm = termOptions.includes(requestedTerm) ? requestedTerm : 'all';
  const hasActiveFilters = selectedYear !== 'all' || selectedTerm !== 'all';

  const filteredProjects = useMemo(() => projectsData.filter((project) => {
    const matchesYear = selectedYear === 'all' || project.years.includes(selectedYear);
    const matchesTerm = selectedTerm === 'all' || project.firstTerms.includes(selectedTerm);

    return matchesYear && matchesTerm;
  }), [selectedYear, selectedTerm]);

  const updateFilter = (key, value) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value === 'all') {
      nextParams.delete(key);
    } else {
      nextParams.set(key, value);
    }

    setSearchParams(nextParams, { replace: true });
  };

  const clearFilters = () => {
    setSearchParams({}, { replace: true });
  };

  return (
    <main className="projects-page">
      <header className="projects-page__intro">
        <h1 className="projects-title">INTEGRA initiatives</h1>
        <p className="projects-page__subtitle">
          Explore the systems, resources, events, and initiatives developed by INTEGRA across FIRST,
          STEM education, and community access.
        </p>
      </header>

      <section className="projects-filter-panel" aria-label="Project filters">
        <div className="projects-filters">
          <FilterGroup
            legend="Year"
            selectedValue={selectedYear}
            onSelect={(value) => updateFilter('year', value)}
            options={[
              { value: 'all', label: 'All years' },
              ...yearOptions.map((year) => ({ value: year, label: year })),
            ]}
          />
          <FilterGroup
            legend="FIRST Role"
            selectedValue={selectedTerm}
            onSelect={(value) => updateFilter('term', value)}
            options={[
              { value: 'all', label: 'All roles' },
              ...termOptions.map((term) => ({ value: term, label: term })),
            ]}
          />
        </div>
      </section>

      <div className="projects-results">
        <div className="projects-results-bar">
          <p aria-live="polite">
            {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
          </p>
          {hasActiveFilters && (
            <button type="button" className="projects-clear-button" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
        <p className="projects-filter-note">
          FIRST role tags reflect INTEGRA&apos;s current documented classification of its contribution to each project.
        </p>
      </div>

      {filteredProjects.length > 0 ? (
        <div
          className="projects-grid"
          role="list"
          aria-label="Filtered projects"
          itemScope
          itemType="https://schema.org/ItemList"
        >
          <meta itemProp="numberOfItems" content={filteredProjects.length.toString()} />
          <meta itemProp="itemListOrder" content="Unordered" />
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className="project-list-item"
              role="listitem"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/Article"
            >
              <meta itemProp="position" content={(index + 1).toString()} />
              <ProjectCard project={project} />
            </div>
          ))}
        </div>
      ) : (
        <section className="projects-empty" aria-live="polite">
          <h2>No projects match these filters.</h2>
          <button type="button" className="projects-clear-button" onClick={clearFilters}>
            Clear filters
          </button>
        </section>
      )}
    </main>
  );
};

export default Projects;
